import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Search, RefreshCw, Mail, MailX, Ban, ExternalLink, ChevronDown, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_COLORS = {
  paid: "bg-green-100 text-green-800",
  pending: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-700",
  refunded: "bg-slate-200 text-slate-600",
  created: "bg-blue-100 text-blue-700",
};
const EMAIL_COLORS = {
  sent: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  not_sent: "bg-slate-100 text-slate-500",
};

export function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [resendDialog, setResendDialog] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchOrders(); }, []);

  async function fetchOrders() {
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  }

  async function handleResend() {
    if (!resendDialog) return;
    setSending(true);
    try {
      await api.post(`/admin/orders/${resendDialog.order_id}/resend`, {
        email: resendEmail.trim() || undefined,
      });
      toast.success("PDF email resent!");
      setResendDialog(null);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    } finally { setSending(false); }
  }

  async function handleRefund(orderId) {
    if (!window.confirm("Mark this order as refunded? The download link will be disabled.")) return;
    try {
      await api.put(`/admin/orders/${orderId}/refund`);
      toast.success("Order marked as refunded");
      fetchOrders();
    } catch { toast.error("Failed to mark refunded"); }
  }

  const filtered = orders.filter((o) => {
    if (filter === "email_failed" && o.email_status !== "failed") return false;
    if (filter === "paid" && o.payment_status !== "paid") return false;
    if (filter === "refunded" && o.payment_status !== "refunded") return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (o.buyer_email || "").toLowerCase().includes(q) ||
        (o.instagram_handle || "").toLowerCase().includes(q) ||
        (o.order_id || "").toLowerCase().includes(q) ||
        (o.pack_title || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  if (loading) return <div className="py-8 text-center text-slate-400">Loading orders...</div>;

  return (
    <div className="mt-4 space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search email, IG handle, order ID..." value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-full text-sm" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          className="rounded-full border border-border bg-white px-3 py-1.5 text-sm font-medium text-slate-700">
          <option value="all">All</option>
          <option value="paid">Paid</option>
          <option value="email_failed">Email Failed</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <p className="text-center py-8 text-slate-400 font-body">No orders found</p>
      ) : (
        filtered.map((o) => (
          <div key={o.order_id || o._id} className="bg-white rounded-2xl p-4 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-sm text-slate-800 truncate">{o.pack_title}</p>
                <p className="text-xs text-slate-500 truncate">{o.buyer_email}</p>
                {o.instagram_handle && (
                  <p className="text-xs text-slate-400">@{o.instagram_handle}</p>
                )}
              </div>
              <span className="font-heading font-bold text-sm text-slate-800 shrink-0">
                ₹{(o.amount || 0) / 100}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`text-[10px] border-0 ${STATUS_COLORS[o.payment_status] || "bg-slate-100"}`}>
                {o.payment_status}
              </Badge>
              <Badge className={`text-[10px] border-0 ${EMAIL_COLORS[o.email_status] || "bg-slate-100"}`}>
                Email: {o.email_status || "not_sent"}
              </Badge>
              <span className="text-[10px] text-slate-400">{o.order_id}</span>
              <span className="text-[10px] text-slate-400">{(o.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="flex gap-2 pt-1">
              {o.payment_status === "paid" && (
                <>
                  <Button size="sm" variant="outline" className="h-7 text-xs rounded-full px-3"
                    onClick={() => { setResendDialog(o); setResendEmail(o.buyer_email || ""); }}>
                    <RefreshCw className="w-3 h-3 mr-1" /> Resend
                  </Button>
                  <Button size="sm" variant="outline"
                    className="h-7 text-xs rounded-full px-3 text-red-500 hover:text-red-700"
                    onClick={() => handleRefund(o.order_id)}>
                    <Ban className="w-3 h-3 mr-1" /> Refund
                  </Button>
                </>
              )}
            </div>
          </div>
        ))
      )}

      {/* Resend Dialog */}
      <Dialog open={!!resendDialog} onOpenChange={(o) => !o && setResendDialog(null)}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Resend PDF Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">
              Resend to {resendDialog?.pack_title}
            </p>
            <Input value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
              placeholder="Buyer's email" className="rounded-xl" />
            <p className="text-xs text-slate-400">Change the email if the buyer gave you a corrected one</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setResendDialog(null)}>Cancel</Button>
            <Button className="bg-coral hover:bg-coral-hover text-white rounded-full" disabled={sending}
              onClick={handleResend}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
